import { ComparisonTable } from '@/components/astro/new-design-25/SEO/comparison-table';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly';
import { SeoContent } from '@/components/astro/new-design-25/SEO/seo-content';
import { TextToSpeechConverter } from '@/components/astro/new-design-25/SEO/text-to-speech-converter';
import { FAQ } from '@/components/astro/new-design-25/ui/FAQ';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { CategoryCard, toolCategories } from '@/routes/_marketing/tools';
import type { Voice } from '@listening/api-contract/legacy-client-types';
import type { AudioExportFormat } from '@/lib/audio-format-export';

const comparisonTableData = {
  title: 'Why Choose Listening Over Other Text-to-Speech Tools?',
  description:
    'Listening is uniquely designed for academic and professional content, offering superior audio quality and specialized features.',
  columns: [
    { header: 'Listening', isHighlighted: true },
    { header: 'Other Tools' },
  ],
  rows: [
    {
      feature: 'Natural-Sounding AI Voices',
      description: 'Realistic, human-like voices for clear and engaging audio.',
      values: ['High-quality AI voices', 'Limited quality'],
    },
    {
      feature: 'Optimized for Academic Content',
      description:
        'Specifically tailored for technical and research documents.',
      values: ['Full optimization', 'Not specialized'],
    },
    {
      feature: 'Accurate Technical Terminology',
      description: 'Expertly handles pronunciation of specialized terms.',
      values: ['95% accuracy', 'Poor pronunciation'],
    },
    {
      feature: 'Flexible Citation Handling',
      description: 'Option to include or exclude citations and references.',
      values: ['Smart citation control', 'Basic handling'],
    },
    {
      feature: 'Cross-Platform Availability',
      description:
        'Accessible via web browsers, mobile apps, and browser extensions.',
      values: ['Web, mobile, extension', 'Limited platforms'],
    },
    {
      feature: 'Enhanced Speed Control',
      description: 'Easily adjust playback speed between 0.5x and 4x.',
      values: ['0.5x to 4x range', 'Limited options'],
    },
    {
      feature: '24-Hour Read-Along',
      description:
        'Open a temporary Listening link with synced text, audio, and controls.',
      values: ['Text view + playbar', 'Audio only'],
    },
    {
      feature: 'Easy Sharing',
      description:
        'Copy the read-along link without putting your text in the URL.',
      values: ['Copyable 24-hour link', 'No sharing'],
    },
  ],
};

type Props = {
  converterHeading?: string;
  converterSubtitle?: string;
  conversionLabel: string;
  downloadFileBaseName?: string;
  faqs?: Array<{ question: string; answer: string }>;
  faqDescription: string;
  faqTitle: string;
  initialFormat?: AudioExportFormat;
  preferredGender?: Voice['gender'];
  preferredLang?: Voice['lang'];
  seoSections?: Array<{
    title: string;
    content: string;
    contentSecondary?: string;
  }>;
};

export function TextToAudioDemoPage({
  converterHeading,
  converterSubtitle,
  conversionLabel,
  downloadFileBaseName,
  faqs,
  faqDescription,
  faqTitle,
  initialFormat,
  preferredGender,
  preferredLang,
  seoSections,
}: Props) {
  const faqItems = faqs ?? [
    {
      question: `How do I convert text to ${conversionLabel}?`,
      answer:
        'Paste or type your text into the editor (up to 10,000 characters), pick a voice, then press Generate audio. Once it finishes, Listening opens a 24-hour read-along link with a synced text view, playbar, speed controls, and copyable sharing.',
    },
    {
      question: 'Which languages are supported?',
      answer:
        "English, Spanish, French, Hindi, Italian, Japanese, Portuguese, and Chinese. The page auto-detects the language of your text and picks a matching voice; if it detects an unsupported language, generation is blocked so you don't get garbled audio.",
    },
    {
      question: 'Can I download the audio?',
      answer:
        'No. The free public tools now create a 24-hour Listening link instead of a downloadable file. The link opens a read-along page where you can play the audio, review the text, change speed, and use the playbar controls.',
    },
    {
      question: 'Is there a character limit?',
      answer:
        'Yes - this free converter is capped at 10,000 characters per generation so it stays fast and free for everyone. Need longer audio? The Listening app converts full documents, PDFs, and pasted text of any length with the same voices.',
    },
    {
      question: 'Can I share my generated audio?',
      answer:
        'Yes. Copy the read-along URL from the result page. It stays available for 24 hours and does not include your pasted text in the page URL.',
    },
  ];

  return (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>
      <LinksSrOnly />

      <div className='relative bg-white'>
        <NavBar />
        <main id='main-content' tabIndex={-1}>
          <TextToSpeechConverter
            downloadFileBaseName={downloadFileBaseName}
            heading={converterHeading}
            initialFormat={initialFormat}
            preferredGender={preferredGender}
            preferredLang={preferredLang}
            subtitle={converterSubtitle}
          />
        </main>
      </div>
      <div className='hidden sm:block'>
        <ComparisonTable
          columns={comparisonTableData.columns}
          description={comparisonTableData.description}
          rows={comparisonTableData.rows}
          title={comparisonTableData.title}
        />
      </div>

      {seoSections && <SeoContent sections={seoSections} />}

      <section
        aria-labelledby='more-listening-tools-heading'
        className='bg-white px-6 py-16 text-[#0f0e0c] sm:px-8 lg:py-20'
      >
        <div className='mx-auto max-w-[1320px]'>
          <div className='mb-9 max-w-3xl'>
            <p className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
              Use cases
            </p>
            <h2
              className='mt-3 text-[clamp(34px,4vw,56px)] leading-[1] font-bold tracking-[-0.035em] text-balance text-[#0f0e0c]'
              id='more-listening-tools-heading'
            >
              Even more ways to use Listening
            </h2>
            <p className='mt-4 max-w-2xl text-[17px] leading-[1.55] text-[#5a564e]'>
              Explore focused landing pages for PDFs, articles, research papers,
              mobile read aloud, study workflows, and accessibility.
            </p>
          </div>
        </div>
        <div className='mx-auto grid max-w-[1320px] grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3'>
          {toolCategories.map((category) => (
            <CategoryCard category={category} key={category.id} />
          ))}
        </div>
      </section>

      <Testimonials />

      <div
        className='bg-linear-to-b from-azure-525/5 to-azure-525/20'
        role='complementary'
      >
        <FAQ
          description={faqDescription}
          faqs={faqItems}
          showApplyButton={false}
          title={faqTitle}
        />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );
}
